import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  AddLineRequest,
  ConsumptionMark,
  CreateManualReceiptRequest,
  CreateReceiptRequest,
  LineItem,
  ReceiptDetail,
  TagConsumerRequest,
  UpdateLineRequest,
  UpdateReceiptRequest,
  VersionedRequest,
} from "@/api/types";
import { queryKeys } from "./queryKeys";
import { useConflictToast } from "./useConflictToast";

export function useReceipts(tripId: string | undefined) {
  return useQuery({
    queryKey: tripId ? queryKeys.receipts(tripId) : ["receipts", "none"],
    queryFn: () => api.listReceipts(tripId!),
    enabled: !!tripId,
  });
}

export function useReceipt(
  tripId: string | undefined,
  receiptId: string | undefined,
) {
  return useQuery({
    queryKey:
      tripId && receiptId
        ? queryKeys.receipt(tripId, receiptId)
        : ["receipt", "none"],
    queryFn: () => api.getReceipt(tripId!, receiptId!),
    enabled: !!tripId && !!receiptId,
    // Poll while parsing — see AC-07.4 + design wireframe receipt-detail.md.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === "parsing" || data.status === "pending_upload")) {
        return 2_000;
      }
      return false;
    },
  });
}

export function useCreateReceipt(tripId: string) {
  return useMutation({
    mutationFn: (body: CreateReceiptRequest) => api.createReceipt(tripId, body),
  });
}

/**
 * US-20 / ADR-0031: create a photo-less manual receipt (born confirmed,
 * source=manual, reflected by construction). On success we seed the returned
 * ReceiptDetail directly into the receipt-detail cache before the caller
 * navigates, so the detail screen renders the receipt with zero flicker and it
 * is never momentarily unreflected. The list + settlements are invalidated so
 * the new (already-reflected) receipt lands in «Розрахунок» immediately.
 */
export function useCreateManualReceipt(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateManualReceiptRequest) =>
      api.createManualReceipt(tripId, body),
    onSuccess: (created) => {
      qc.setQueryData<ReceiptDetail>(
        queryKeys.receipt(tripId, created.receipt_id),
        created,
      );
      void qc.invalidateQueries({ queryKey: queryKeys.receipts(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.settlements(tripId) });
    },
  });
}

export function useConfirmReceipt(tripId: string, receiptId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (body: VersionedRequest) =>
      api.confirmReceipt(tripId, receiptId, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.receipt(tripId, receiptId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
    },
    onError: (err) =>
      onConflict(err, [queryKeys.receipt(tripId, receiptId)]),
  });
}

/**
 * Toggle a consumer on a line. Optimistic — visual change is instant,
 * any 409 reverts and surfaces the passive ConflictToast.
 */
export function useToggleConsumer(tripId: string, receiptId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  const key = queryKeys.receipt(tripId, receiptId);

  return useMutation({
    mutationFn: async (input: {
      lineId: string;
      participantId: string;
      currentlyTagged: boolean;
      quantity?: number | null;
      lineVersion: number;
    }) => {
      if (input.currentlyTagged) {
        await api.untagConsumer(tripId, receiptId, input.lineId, input.participantId);
        return null;
      }
      const body: TagConsumerRequest =
        input.quantity != null ? { quantity: input.quantity } : {};
      return api.tagConsumer(
        tripId,
        receiptId,
        input.lineId,
        input.participantId,
        body,
      );
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ReceiptDetail>(key);
      if (prev) {
        qc.setQueryData<ReceiptDetail>(key, applyConsumerToggle(prev, input));
      }
      return { prev };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      onConflict(err, [key]);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
    },
  });
}

/**
 * US-08: edit a parsed line. Optimistically patches the cached line, then
 * lets the server response (or 409 rollback) reconcile. Mirrors
 * useToggleConsumer to keep the conflict-toast flow consistent.
 */
export function useUpdateLine(tripId: string, receiptId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  const key = queryKeys.receipt(tripId, receiptId);

  return useMutation({
    mutationFn: (input: { lineId: string; patch: UpdateLineRequest }) =>
      api.updateLine(tripId, receiptId, input.lineId, input.patch),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ReceiptDetail>(key);
      if (prev) {
        qc.setQueryData<ReceiptDetail>(key, applyLineEdit(prev, input));
      }
      return { prev };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      onConflict(err, [key]);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
    },
  });
}

/**
 * US-08 (delete part): remove an entire line. Optimistic — the row
 * disappears immediately; a 409/404 rollback restores it.
 */
export function useDeleteLine(tripId: string, receiptId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  const key = queryKeys.receipt(tripId, receiptId);

  return useMutation({
    mutationFn: (input: { lineId: string }) =>
      api.deleteLine(tripId, receiptId, input.lineId),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ReceiptDetail>(key);
      if (prev) {
        qc.setQueryData<ReceiptDetail>(key, {
          ...prev,
          lines: (prev.lines ?? []).filter((l) => l.line_id !== input.lineId),
        });
      }
      return { prev };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      onConflict(err, [key]);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
    },
  });
}

/**
 * US-09: add a missing line manually. Server mints the real line_id; we
 * append a temporary row with a placeholder id so the user sees it
 * instantly, then swap on success.
 */
export function useAddLine(tripId: string, receiptId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  const key = queryKeys.receipt(tripId, receiptId);

  return useMutation({
    mutationFn: (body: AddLineRequest) => api.addLine(tripId, receiptId, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ReceiptDetail>(key);
      const tempId = `__pending-${Date.now()}`;
      if (prev) {
        const optimistic: LineItem = {
          line_id: tempId,
          name: body.name,
          translated_name: null,
          quantity: body.quantity,
          unit_price: body.unit_price,
          line_total: body.quantity * body.unit_price,
          shared_by_all: false,
          consumers: [],
          version: 1,
        };
        qc.setQueryData<ReceiptDetail>(key, {
          ...prev,
          lines: [...(prev.lines ?? []), optimistic],
        });
      }
      return { prev, tempId };
    },
    onError: (err, _body, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      onConflict(err, [key]);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
    },
  });
}

/**
 * Set receipt-level fields (paid_by_participant_id / source_ccy) — the
 * resolution path for the no-payer / no-currency block-gate buckets
 * (ADR-0029, AC-03.8). Invalidates the receipts list so the readiness count
 * and close gate re-derive, plus the plan so the now-reflected receipt lands
 * in it (AC-16.5).
 */
export function useUpdateReceiptFields(tripId: string, receiptId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (body: UpdateReceiptRequest) =>
      api.updateReceipt(tripId, receiptId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipt(tripId, receiptId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.settlements(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
    },
    onError: (err) => onConflict(err, [queryKeys.receipt(tripId, receiptId)]),
  });
}

/**
 * Retry AI parsing of a `parse_failed` receipt (resolution path for the
 * R-FAILED bucket; ADR-0021 retry-only). Invalidates the list so the badge
 * and readiness count reflect the transition.
 */
export function useRetryParse(tripId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (receiptId: string) => api.parseReceipt(tripId, receiptId),
    onSuccess: (_data, receiptId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipt(tripId, receiptId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts(tripId) });
    },
    onError: (err) => onConflict(err, [queryKeys.receipts(tripId)]),
  });
}

/**
 * Delete a receipt — the universal escape hatch so a trip can never become
 * permanently uncloseable (ADR-0029, AC-03.8). Invalidates the list, plan and
 * balances so the readiness count drops and the gate re-evaluates.
 */
export function useDeleteReceipt(tripId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (receiptId: string) => api.deleteReceipt(tripId, receiptId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.settlements(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
    },
    onError: (err) => onConflict(err, [queryKeys.receipts(tripId)]),
  });
}

function applyLineEdit(
  detail: ReceiptDetail,
  input: { lineId: string; patch: UpdateLineRequest },
): ReceiptDetail {
  const lines: LineItem[] = (detail.lines ?? []).map((l) => {
    if (l.line_id !== input.lineId) return l;
    const next: LineItem = {
      ...l,
      name: input.patch.name ?? l.name,
      quantity: input.patch.quantity ?? l.quantity,
      unit_price: input.patch.unit_price ?? l.unit_price,
    };
    next.line_total = next.quantity * next.unit_price;
    return next;
  });
  return { ...detail, lines };
}

function applyConsumerToggle(
  detail: ReceiptDetail,
  input: {
    lineId: string;
    participantId: string;
    currentlyTagged: boolean;
    quantity?: number | null;
  },
): ReceiptDetail {
  const lines: LineItem[] = (detail.lines ?? []).map((l) => {
    if (l.line_id !== input.lineId) return l;
    const consumers: ConsumptionMark[] = l.consumers ?? [];
    if (input.currentlyTagged) {
      return {
        ...l,
        consumers: consumers.filter((c) => c.participant_id !== input.participantId),
      };
    }
    return {
      ...l,
      consumers: [
        ...consumers.filter((c) => c.participant_id !== input.participantId),
        {
          participant_id: input.participantId,
          quantity: input.quantity ?? null,
          created_at: new Date().toISOString(),
        },
      ],
    };
  });
  return { ...detail, lines };
}
